import { useMutation, useQuery } from "@tanstack/react-query";
import Servico from "../../../Models/Financeiro/Faturamento/Servico";
import RequestUtilitario, { BasicMutationAxiosType, BasicQueryType } from "../../../Utils/RequisicaoUtilitario";
import { Axios } from "../../Axios";
import { queryClient } from "../../../App";
import { AxiosResponse } from "axios";
import RegimeTributario from "../../../Models/Financeiro/Faturamento/Imposto/RegimeTributario";
import Utilitario from "../../../Utils/Utilitario";

export function useSalvar({
    onSuccess,
    queryKey,
    ...rest
}: BasicMutationAxiosType<
    {servico:Servico, isAlteracao:boolean},
    {},
    number|void
>) {
    return useMutation({
        ...rest,
        mutationFn: ({
            isAlteracao,
            servico
        }) => {            
            return (isAlteracao ? Axios.put<void>("financeiro/faturamento/servico", servico) : Axios.post<number>("financeiro/faturamento/servico", servico)).then(t => t.data);
        },
        onSuccess : (retorno, entrada, context) => {
            queryClient.invalidateQueries({ queryKey: ["Financeiro.Faturamento.Servico.Listar"] });
            queryClient.invalidateQueries({ queryKey: [`Financeiro.Faturamento.Servico.Buscar.${entrada.isAlteracao ? entrada.servico.codigo : retorno}`] });
            onSuccess?.(retorno, entrada, context);
        }
    });
}
export function useDesativar({
    onSuccess,
    queryKey,
    ...rest
}:BasicMutationAxiosType<
    number, 
    unknown,
    Promise<AxiosResponse<void, any>>
>) {
    return useMutation({
        ...rest,
        mutationFn : (data) => Axios.delete(`financeiro/faturamento/servico/${data}`),
        onSuccess : (...data) => {
            queryClient.invalidateQueries({ queryKey: ["Financeiro.Faturamento.Servico.Listar"] });
            queryClient.invalidateQueries({ queryKey: [`Financeiro.Faturamento.Servico.Buscar.${data}`] });
            onSuccess?.(...data);
        },
    });
}

export function useAtivar({
    onSuccess,
    queryKey,
    ...rest
}: BasicMutationAxiosType<
    number,
    unknown,
    Promise<AxiosResponse<void, any>>
>) {
    return useMutation({
        ...rest,
        mutationFn: (data) => Axios.patch(`financeiro/faturamento/servico/ativar/${data}`),
        onSuccess: (...data) => {
            queryClient.invalidateQueries({ queryKey: ["Financeiro.Faturamento.Servico.Listar"] });
            queryClient.invalidateQueries({ queryKey: [`Financeiro.Faturamento.Servico.Buscar.${data}`] });
            onSuccess?.(...data);
        }
    });
}

export function useListar({
    servico,
    ativo,
    queryKey,
    ...reset
}: BasicQueryType<AxiosResponse<Servico[]>, {
    servico?: Servico,
    ativo?: boolean
}>) {
    const queryString = RequestUtilitario.objectToQueryString({
        ...(servico ?? {}),
        servicoAtivo : ativo
    });

    return useQuery({
        ...reset,
        queryKey: ["Financeiro.Faturamento.Servico.Listar", `Financeiro.Faturamento.Servico.Listar?${queryString}`],
        queryFn: () => Axios.get<Servico[]>(`financeiro/faturamento/servico?${queryString}`),
        select : ({ data }) => {
            return data.map(m => {
                m.dataCriacao = new Date(m.dataCriacao);
                return m;
            });
        },
        staleTime: 1000 * 60 * 1 // query ficará valida por 1 min
    });
}
export function useBuscar({
    queryKey,
    codigo,
    ...rest
}:BasicQueryType<AxiosResponse<Servico|null>, {codigo:number}>) {
    return useQuery({
        ...rest,
        queryKey : [`Financeiro.Faturamento.Servico.Buscar.${codigo}`],
        queryFn : () => Axios.get<Servico|null>(`financeiro/faturamento/servico/${codigo}`),
        select : ({ data }) => {
            if (data === null) return null;

            data.dataCriacao = new Date(data.dataCriacao);
            data.valoresImpostos = (data.valoresImpostos ?? []).map(m => {
                m.dataCriacao = new Date(m.dataCriacao);
                if (m.valor != null)
                {
                    m.valor.dataCriacao = new Date(m.valor.dataCriacao);
                    m.valor.dataInicial = new Date(m.valor.dataInicial);
                }
                return m;
            });
            data.regimesTributarios = Utilitario.groupBy(data.valoresImpostos, (valorServico) => valorServico.valor?.codRegimeTributario).map(m => {
                return {
                    codigo : m.chave
                } as RegimeTributario
            });

            return data;
        },
        staleTime : 1000 * 60 * 0.5
    });
}
export default {
    useSalvar,
    useDesativar,
    useAtivar,
    useListar,
    useBuscar
}